import { ActionIcon, Box, Group, Paper, ScrollArea, Text } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import {
  REFINEMENT_RAIL_WIDTH,
  REFINEMENT_SURFACE_Z_INDEX,
} from '../constants/refinement-layout'
import type { ReactNode } from 'react'
import { HEADER_HEIGHT } from '@/features/layout/context'

export interface RefinementRailPanelProps {
  /** Accessible name for the fixed aside (e.g. "Plan personalization"). */
  ariaLabel: string
  /** Short title in the header row. */
  title: string
  onClose: () => void
  closeAriaLabel: string
  /** Nodes before the title (e.g. sparkles icon). */
  headerPrefix?: ReactNode
  /** Nodes after the title (e.g. tier badge). */
  headerSuffix?: ReactNode
  children: ReactNode
}

/**
 * Shared fixed right rail for Q&A and AI refinement surfaces (desktop ≥1024px).
 * Matches D-014: `HEADER_HEIGHT` offset, `REFINEMENT_RAIL_WIDTH`, `REFINEMENT_SURFACE_Z_INDEX`.
 */
export function RefinementRailPanel({
  ariaLabel,
  title,
  onClose,
  closeAriaLabel,
  headerPrefix,
  headerSuffix,
  children,
}: RefinementRailPanelProps) {
  return (
    <Box
      pos="fixed"
      right={0}
      top={HEADER_HEIGHT}
      w={REFINEMENT_RAIL_WIDTH}
      mah={`calc(100dvh - ${HEADER_HEIGHT}px)`}
      p={0}
      style={{
        zIndex: REFINEMENT_SURFACE_Z_INDEX,
        display: 'flex',
        flexDirection: 'column',
      }}
      component="aside"
      aria-label={ariaLabel}
    >
      <Paper
        p="md"
        radius={0}
        h="100%"
        withBorder
        style={{
          borderLeft: '1px solid var(--mantine-color-default-border)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Group justify="space-between" align="center" mb="sm" wrap="nowrap" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            {headerPrefix}
            <Text size="sm" fw={600} lineClamp={1}>
              {title}
            </Text>
            {headerSuffix}
          </Group>
          <ActionIcon variant="subtle" size="md" onClick={onClose} aria-label={closeAriaLabel}>
            <IconX size={18} />
          </ActionIcon>
        </Group>
        <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
          {children}
        </ScrollArea>
      </Paper>
    </Box>
  )
}
